import { GetStaticProps, InferGetStaticPropsType } from 'next';
import Image from 'next/image';

import { Header } from '../components/Header';
import { PageMetadata } from '../components/PageMetadata';
import { Footer } from '../components/Footer/Footer';
import { Section, Row, Column } from '../components/Section';
import DentalBlueCopy from '../copy/dental/dental-blue.md';
import DentalBluePpoCopy from '../copy/dental/dental-blue-ppo.md';
import { CallToActionSection } from '../components/CallToActionSection';
import dentalBlueLogo from '../public/static/images/dental-blue.svg';
import dentalBluePpoLogo from '../public/static/images/dental-blue-ppo.svg';
import { GetServerPageProps } from '../lib/apollo';
import {
  getServerPageDental,
  getServerPageDentalHeroSection,
} from './Dental.generated';
import { CommonQuestions } from '../components/CommonQuestions/CommonQuestions';
import { Hero } from '../components/Hero';
import { heroData } from '../components/heroData';

export default function Dental({
  pageMetadata,
  heroData,
  dentalHeroData,
  commonQuestionsSnippet,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  if (!pageMetadata) {
    throw new Error('Unable to load page');
  }
  return (
    <>
      <PageMetadata
        pageTitle={pageMetadata.seoTitle ?? undefined}
        description={pageMetadata.seoDescription ?? undefined}
        openGraphImage={pageMetadata.openGraphImage?.url ?? undefined}
      />
      <Header planType="dental" />
      <Hero
        heading={
          dentalHeroData.snippetCollection?.items[0]?.name ?? heroData.heading
        }
        subheading={heroData.subheading}
        image={{
          url: pageMetadata.heroImage?.url ?? '',
          description: pageMetadata.heroImage?.description ?? '',
        }}
        aggregatedReviews={heroData.aggregatedReviews}
        defaultPlanTypeOption="dental"
      />
      <div className="w-full relative lg:pt-8 bg-gray-200">
        <div className="container mx-auto mb-6 px-4 flex flex-wrap justify-center">
          <Image
            src={dentalBlueLogo}
            className="mx-4 my-3"
            alt="Dental Blue for Individuals"
          />
          <Image
            src={dentalBluePpoLogo}
            className="mx-4 my-3"
            alt="Dental Blue for Individuals PPO"
          />
        </div>
        <CallToActionSection planType="dental" quoteAction />
        <Section background="WHITE">
          <Row inset={true}>
            <Column>
              <Image
                src={dentalBlueLogo}
                alt="Dental Blue for Individuals"
                className="mx-auto"
                height={53}
              />
            </Column>
          </Row>
          <Row inset={true}>
            <Column>
              <DentalBlueCopy />
            </Column>
          </Row>
        </Section>
        <Section background="GRAY">
          <Row inset={true}>
            <Column>
              <Image
                src={dentalBluePpoLogo}
                alt="Dental Blue for Individuals PPO"
                className="mx-auto"
                height={53}
              />
            </Column>
          </Row>
          <Row inset={true}>
            <Column>
              <DentalBluePpoCopy />
            </Column>
          </Row>
        </Section>
        <CommonQuestions snippetGroup={commonQuestionsSnippet} inset />
        <CallToActionSection planType="dental" quoteAction />
        <Footer />
      </div>
    </>
  );
}

export const getStaticProps: GetStaticProps<{
  pageMetadata:
    | NonNullable<
        NonNullable<
          GetServerPageProps<typeof getServerPageDental>['data']
        >['pageCollection']
      >['items'][0]
    | undefined;
  heroData: Awaited<ReturnType<typeof heroData>>;
  dentalHeroData: GetServerPageProps<
    typeof getServerPageDentalHeroSection
  >['data'];
  commonQuestionsSnippet:
    | NonNullable<
        NonNullable<
          GetServerPageProps<typeof getServerPageDental>['data']
        >['snippetGroupCollection']
      >['items'][0]
    | undefined;
}> = async () => {
  const { data } = (await getServerPageDental({})).props;

  return {
    props: {
      pageMetadata: data.pageCollection?.items[0],
      heroData: await heroData(),
      dentalHeroData: (await getServerPageDentalHeroSection({})).props.data,
      commonQuestionsSnippet: data.snippetGroupCollection?.items[0],
    },
  };
};
